import {DateTime} from 'luxon';
import {ActionCompletionReason, ActionItemWireDto, ActionSeverity} from '../shell-communication/action-board-operations';

// CR-20260922-001 — the board's pure rules (system description §5.4 – §5.6), kept free of Angular so
// they read as the spec and can be checked by hand.

/** Global is the stable protocol/storage path; it says nothing about the recipient audience. */
export const GENERAL_MESSAGE_LABEL = 'General Message';

export function actionSubjectLabel(subject: {subjectPath: string, subjectLabel: string}): string {
    return subject.subjectPath === 'Global' ? GENERAL_MESSAGE_LABEL : subject.subjectLabel;
}

export const SEVERITY_RANK: { [s: string]: number } = {red: 3, orange: 2, yellow: 1};

export function severityRank(s: string): number {
    return SEVERITY_RANK[s] || 0;
}

/** Attention includes every live item, including optional green opportunities. */
export function isOutstanding(item: ActionItemWireDto): boolean {
    return !item.completedAtUtc && !item.isStale;
}

export function isCompleted(item: ActionItemWireDto): boolean {
    return !!item.completedAtUtc;
}

/** Live items contribute to attention, independently of overdue eligibility. */
export function isActive(item: ActionItemWireDto): boolean {
    return !item.completedAtUtc && !item.isStale;
}

function activeSeverityRank(severity: string): number {
    return ({red: 3, orange: 2, green: 1})[severity] || 0;
}

/** Whole calendar days between two "yyyy-MM-dd" keys (b - a). */
export function daysBetween(a: string, b: string): number {
    const da = DateTime.fromISO(a);
    const db = DateTime.fromISO(b);
    if (!da.isValid || !db.isValid) {
        return 0;
    }
    return Math.round(db.diff(da, 'days').days);
}

/** Overdue days, measured from the DUE day, not from delivery (§5.4). 0 = not overdue. */
export function overdueDays(item: ActionItemWireDto, todayDayKey: string): number {
    if (!isOutstanding(item) || item.itemType === 'AvailableAdjustment') {
        return 0;
    }
    const d = daysBetween(item.dueDayKey, todayDayKey);
    return d > 0 ? d : 0;
}

/** The day block an item sits under: today while outstanding, its completion day once done (§5.6). */
export function effectiveDayKey(item: ActionItemWireDto, todayDayKey: string): string {
    if (isCompleted(item) && item.completedDayKey) {
        return item.completedDayKey;
    }
    return todayDayKey;
}

/**
 * Activity first; active groups red → orange → green → other. The previous overdue,
 * severity and age rules break ties only inside those groups. Completed items retain their order.
 */
export function compareWithinDay(a: ActionItemWireDto, b: ActionItemWireDto, todayDayKey: string): number {
    const activeA = isActive(a), activeB = isActive(b);
    if (activeA !== activeB) { return activeA ? -1 : 1; }
    if (activeA) {
        const rank = activeSeverityRank(b.severity) - activeSeverityRank(a.severity);
        if (rank) { return rank; }
    }
    const ca = isCompleted(a) ? 1 : 0;
    const cb = isCompleted(b) ? 1 : 0;
    if (ca !== cb) {
        return ca - cb;
    }

    if (ca === 1) {
        return (b.completedAtUtc || '').localeCompare(a.completedAtUtc || '');
    }

    const oa = overdueDays(a, todayDayKey) > 0 ? 1 : 0;
    const ob = overdueDays(b, todayDayKey) > 0 ? 1 : 0;
    if (oa !== ob) {
        return ob - oa;
    }

    const sa = severityRank(a.severity);
    const sb = severityRank(b.severity);
    if (sa !== sb) {
        return sb - sa;
    }

    return (a.originatedAtUtc || '').localeCompare(b.originatedAtUtc || '');
}

export interface DayBlock {
    dayKey: string;
    heading: string;
    items: ActionItemWireDto[];
    /** Live items plus the completed ones that did NOT end well (missed, withdrawn) —
     * always listed (owner 2026-09-23). */
    visible: ActionItemWireDto[];
    /** Completed well — satisfied or acknowledged — folded into the day's collapsible drawer
     * (owner 2026-09-23). */
    done: ActionItemWireDto[];
    outstanding: number;
    overdue: number;
    closed: number;
}

/** Done = completed WELL: satisfied by a run, or a notice acknowledged (owner 2026-09-23: both
 * go into the drawer; missed / stale / withdrawn stay in the list). */
export function isDone(item: ActionItemWireDto): boolean {
    return !!item.completedAtUtc && (item.completionReason === 'satisfied' || item.completionReason === 'acknowledged');
}

/** Day blocks, newest first; a day with no items has no block. */
export function groupByDay(items: ActionItemWireDto[], todayDayKey: string): DayBlock[] {
    const byDay = new Map<string, ActionItemWireDto[]>();

    for (const item of items) {
        const day = effectiveDayKey(item, todayDayKey);
        if (!byDay.has(day)) {
            byDay.set(day, []);
        }
        byDay.get(day).push(item);
    }

    const keys = Array.from(byDay.keys()).sort((a, b) => {
        const active = Number(byDay.get(b).some(isActive)) - Number(byDay.get(a).some(isActive));
        return active || b.localeCompare(a);
    });

    return keys.map(dayKey => {
        const dayItems = byDay.get(dayKey).slice().sort((a, b) => compareWithinDay(a, b, todayDayKey));
        return {
            dayKey,
            heading: dayHeading(dayKey, todayDayKey),
            items: dayItems,
            visible: dayItems.filter(x => !isDone(x)),
            done: dayItems.filter(isDone),
            outstanding: dayItems.filter(isOutstanding).length,
            overdue: dayItems.filter(x => overdueDays(x, todayDayKey) > 0).length,
            closed: dayItems.filter(isCompleted).length
        };
    });
}

export function dayHeading(dayKey: string, todayDayKey: string): string {
    // Capitalised, not uppercase (owner 2026-09-23): "Today · Tue 22-Sep-26".
    const d = DateTime.fromISO(dayKey);
    const label = d.isValid ? d.toFormat('ccc dd-LLL-yy') : dayKey;
    const delta = daysBetween(dayKey, todayDayKey);
    if (delta === 0) {
        return `Today · ${label}`;
    }
    if (delta === 1) {
        return `Yesterday · ${label}`;
    }
    return label;
}

/**
 * A scope tab (§5.6): the first two segments of a subject path ("APG/{id}", "HedgingGrid/{id}"),
 * generated from the paths present in LIVE items. Global makes no tab and shows under every tab.
 */
export interface ScopeTab {
    key: string;      // path prefix with ids, or '' for All
    label: string;    // "APG · Ports-3"
    outstanding: number;
}

export function scopePrefix(subjectPath: string): string {
    const parts = (subjectPath || '').split('/');
    return parts.length >= 2 ? `${parts[0]}/${parts[1]}` : parts[0] || '';
}

export function scopeLabel(subjectLabel: string): string {
    const parts = (subjectLabel || '').split('/');
    const root = parts[0] === 'HedgingGrid' ? 'Hedging' : parts[0];
    return parts.length >= 2 ? `${root} · ${parts[1]}` : root;
}

// ---------------------------------------------------------------- source + portfolio filters
// Owner screencast 2026-09-23: two linked dropdowns replace the scope tabs. Source first (All
// sources / APG / Hedging Grid / Alerts), then the portfolio, shaped by the source.

/** 'all' — never '' : the DevExtreme select box treats an empty string as "no value". */
export const ALL_KEY = 'all';

export type SourceKey = 'all' | 'APG' | 'HedgingGrid' | 'Alerts' | 'PHAnalysis';

export interface FilterOption {
    key: string;
    label: string;
    outstanding: number;
    /** What the dropdown shows: the label with the outstanding count in brackets when > 0. */
    text: string;
}

function option(key: string, label: string, outstanding: number): FilterOption {
    return {key, label, outstanding, text: outstanding > 0 ? `${label} (${outstanding})` : label};
}

/**
 * Notices are ALERTS (owner 2026-09-23: no longer the sender's name). Everything else is placed
 * by the KIND of its portfolio, not by the producer (owner screencast 2026-09-23, second ruling):
 * an APG portfolio and its linked hedge book belong to the APG source; an independent hedge book
 * belongs to the Hedging Grid source. The producer still owns the card chip and the deep link.
 * This is the item's HOME source. Membership is wider: the Hedging Grid source ALSO takes the
 * APG-dependent hedge books (owner 2026-09-27, both faces) — see matchesSource. The archive applies
 * the same rule on the server (ArchiveFilter.cs).
 */
export function itemSource(item: ActionItemWireDto): SourceKey {
    if (item.itemType === 'Notice') {
        return 'Alerts';
    }
    const root = (item.subjectPath || '').split('/')[0];
    // CR-20260930-001 M4: the analysis-session cards are their own source.
    if (root === 'PHAnalysis') {
        return 'PHAnalysis';
    }
    if (root === 'HedgingGrid') {
        return 'HedgingGrid';
    }
    if (root === 'APG') {
        return 'APG';
    }
    // Unknown root (Global without a notice type, or a future subject): fall back to the producer.
    if (item.source === 'PH ANALYSIS') {
        return 'PHAnalysis';
    }
    return item.source === 'HEDGING GRID' ? 'HedgingGrid' : 'APG';
}

export function sourceLabel(source: SourceKey): string {
    switch (source) {
        case 'APG':
            return 'APG';
        case 'HedgingGrid':
            return 'Hedging Grid';
        case 'Alerts':
            return 'Alerts';
        case 'PHAnalysis':
            return 'PH Analysis';
        default:
            return 'All sources';   // ALL_KEY
    }
}

/** The chip on a card: ALERTS for a notice, the producer's label otherwise. */
export function sourceChip(item: ActionItemWireDto): string {
    return item.itemType === 'Notice' ? 'ALERTS' : item.sourceLabel;
}

/**
 * The portfolio an item belongs to: "APG/{id}" for an APG portfolio AND its hedge book (they are
 * one portfolio — owner 2026-09-23), "HedgingGrid/{id}" for an independent hedge book, "Global".
 */
export function portfolioKey(item: ActionItemWireDto): string {
    return scopePrefix(item.subjectPath);
}

/**
 * "APG · Ports-3" / "Hedging · Book-1" / "Global" — the kind always shown, so the two are told apart.
 * Under the Hedging Grid source an APG portfolio appears through its hedge book only, so the entry
 * reads "APG · Ports-3 · Hedging" there (owner 2026-09-27); the key stays the portfolio key.
 */
export function portfolioOptionLabel(item: ActionItemWireDto, source: SourceKey = ALL_KEY): string {
    if (item.subjectPath === 'Global') {
        return GENERAL_MESSAGE_LABEL;
    }
    const label = scopeLabel(item.subjectLabel);
    return source === 'HedgingGrid' && isApgHedgeBook(item) ? `${label} · Hedging` : label;
}

/** An APG portfolio's hedge book: "APG/{id}/Hedging" (the same shape the producers write). */
export function isApgHedgeBook(item: ActionItemWireDto): boolean {
    const parts = (item.subjectPath || '').split('/');
    return parts.length === 3 && parts[0] === 'APG' && parts[2] === 'Hedging';
}

/**
 * Source membership. The Hedging Grid source is EVERY hedge book: the independent ones AND the
 * APG-dependent ones (owner 2026-09-27) — so an APG hedge book answers to both APG and Hedging Grid.
 */
export function matchesSource(item: ActionItemWireDto, source: SourceKey): boolean {
    if (!source || source === ALL_KEY) {
        return true;
    }
    if (source === 'HedgingGrid' && item.itemType !== 'Notice' && isApgHedgeBook(item)) {
        return true;
    }
    return itemSource(item) === source;
}

/** Global alerts show under every portfolio choice. */
export function matchesPortfolio(item: ActionItemWireDto, portfolio: string): boolean {
    if (!portfolio || portfolio === ALL_KEY) {
        return true;
    }
    if (item.subjectPath === 'Global') {
        return true;
    }
    return portfolioKey(item) === portfolio;
}

export function matchesFilter(item: ActionItemWireDto, source: SourceKey, portfolio: string): boolean {
    return matchesSource(item, source) && matchesPortfolio(item, portfolio);
}

/** The fixed source list, each with its outstanding count over the whole board. */
export function buildSourceOptions(items: ActionItemWireDto[]): FilterOption[] {
    const live = items.filter(isOutstanding);
    const keys: SourceKey[] = [ALL_KEY, 'APG', 'HedgingGrid', 'PHAnalysis', 'Alerts'];
    return keys.map(key => option(key, sourceLabel(key), live.filter(x => matchesSource(x, key)).length));
}

/**
 * The portfolios that have items on the board (live or closed — owner 2026-09-23) under the chosen
 * source, plus "All portfolios" first. Global appears as an entry only under Alerts.
 *
 * ownerOf (CR-20260929-002, owner 2026-09-29): in the All view portfolios of different clients look
 * alike, so each label is prefixed with its client — "Bob P./APG/Ports-3". Global stays bare: it spans clients.
 */
export function buildPortfolioOptions(items: ActionItemWireDto[], source: SourceKey,
                                      ownerOf: ((recipientUserId: string) => string | null) | null = null): FilterOption[] {
    const inSource = items.filter(x => matchesSource(x, source));
    const live = inSource.filter(isOutstanding);
    const labels = new Map<string, string>();

    for (const item of inSource) {
        if (item.subjectPath === 'Global' && source !== 'Alerts') {
            continue;
        }
        const key = portfolioKey(item);
        if (!labels.has(key)) {
            labels.set(key, withOwner(portfolioOptionLabel(item, source), item.subjectPath === 'Global' || !ownerOf ? null : ownerOf(item.recipientUserId)));
        }
    }

    const list = Array.from(labels.entries())
        .map(([key, label]) => option(key, label,
            live.filter(x => key === 'Global' ? x.subjectPath === 'Global' : portfolioKey(x) === key).length))
        .sort((a, b) => a.label.localeCompare(b.label));
    list.unshift(option(ALL_KEY, 'All portfolios', live.length));
    return list;
}

/** "Bob P./APG/Ports-3" — the client prefix of the All view (CR-20260929-002); bare when no owner. */
export function withOwner(label: string, owner: string | null): string {
    return owner ? `${owner}/${label}` : label;
}

/** Tab matches its path as a prefix on a segment boundary; Global matches every tab. */
export function inScope(item: ActionItemWireDto, scopeKey: string): boolean {
    if (!scopeKey) {
        return true;
    }
    if (item.subjectPath === 'Global') {
        return true;
    }
    return item.subjectPath === scopeKey || item.subjectPath.startsWith(scopeKey + '/');
}

export function buildScopeTabs(items: ActionItemWireDto[]): ScopeTab[] {
    const live = items.filter(isOutstanding);
    const tabs = new Map<string, ScopeTab>();

    for (const item of live) {
        if (item.subjectPath === 'Global') {
            continue;
        }
        const key = scopePrefix(item.subjectPath);
        if (!tabs.has(key)) {
            tabs.set(key, {key, label: scopeLabel(item.subjectLabel), outstanding: 0});
        }
    }

    for (const tab of Array.from(tabs.values())) {
        tab.outstanding = live.filter(x => inScope(x, tab.key)).length;
    }

    const list = Array.from(tabs.values()).sort((a, b) => a.label.localeCompare(b.label));
    list.unshift({key: '', label: 'All', outstanding: live.length});
    return list;
}

export function highestSeverity(items: ActionItemWireDto[]): ActionSeverity | '' {
    const overlayRanks = {red: 4, orange: 3, yellow: 2, green: 1};
    let best: ActionSeverity | '' = '';
    for (const item of items) {
        if (!isOutstanding(item)) {
            continue;
        }
        if ((overlayRanks[item.severity] || 0) > (overlayRanks[best] || 0)) {
            best = item.severity;
        }
    }
    return best;
}

/** Severity LEVEL names for text (owner 2026-09-22): never the colour words. */
export function severityLabel(severity: string): string {
    switch (severity) {
        case 'green':
            return 'OPPORTUNITY';
        case 'red':
            return 'CRITICAL';
        case 'orange':
            return 'WARNING';
        case 'yellow':
            return 'REGULAR';
        default:
            return (severity || '').toUpperCase();
    }
}

export function completionTag(item: ActionItemWireDto): string {
    return completionReasonTag(item.completionReason);
}

// ---------------------------------------------------------------- the Archive face
// Owner screencast 2026-09-23: a flat, paged list of EVERY completed item, filtered on the server
// by completion date, severity, completion state, source, portfolio and a search over the text.

export const ARCHIVE_SEVERITY_OPTIONS: { key: ActionSeverity; text: string }[] = [
    {key: 'red', text: severityLabel('red')},
    {key: 'orange', text: severityLabel('orange')},
    {key: 'yellow', text: severityLabel('yellow')},
    {key: 'green', text: severityLabel('green')}
];

/** The picker hands back an ISO instant (or null); the server wants a "yyyy-MM-dd" day key. */
export function pickerValueToDayKey(value: string | null): string | null {
    if (!value) {
        return null;
    }
    const d = DateTime.fromISO(value);
    return d.isValid ? d.toFormat('yyyy-MM-dd') : null;
}

export function completionReasonTag(reason: ActionCompletionReason | string | null): string {
    switch (reason) {
        case 'satisfied':
            return 'DONE';
        case 'stale':
            return 'MISSED';
        case 'acknowledged':
            return "ACK'D";
        case 'withdrawn':
            return 'NOT APPLICABLE';
        default:
            return '';
    }
}

/**
 * ISO UTC instant → "ccc dd-LLL-yy hh:mm a" (12-hour, AM/PM — owner 2026-09-22; dd-MMM-yy) in the given IANA zone — the user's configured default
 * time zone (owner 2026-09-22: every timestamp on the board is in the zone the user selected in
 * the GUI, served by the platform's timestamps provider). Falls back to the browser's zone only
 * when no zone is given.
 */
export function formatInstant(iso: string | null, zone?: string | null): string {
    if (!iso) {
        return '';
    }
    const utc = DateTime.fromISO(iso, {setZone: true});
    if (!utc.isValid) {
        return iso;
    }
    const d = zone ? utc.setZone(zone) : utc.toLocal();
    return (d.isValid ? d : utc.toLocal()).toFormat('ccc dd-LLL-yy hh:mm a');
}

/** "2026-09-22" → "Tue 22-Sep-26" (dd-MMM-yy with the weekday, as the day header). */
export function formatDayKey(dayKey: string | null): string {
    if (!dayKey) {
        return '';
    }
    const d = DateTime.fromISO(dayKey);
    return d.isValid ? d.toFormat('ccc dd-LLL-yy') : dayKey;
}

export interface ActionItemPayload {
    health?: string;
    availability?: string;
    hedgeLabel?: string;
    portfolioId?: string;
    portfolioName?: string;
    underlying?: string;
    expirationDayKey?: string;
    /** ZoneDrift: the market zone the checker judged against, and why the book is out. */
    zone?: number;
    reason?: string;
    legs?: { ticker: string; qty: number; optionType: string; strike: number }[];
    /** CR-20260930-001 M4: the analysis-session cards carry the session/combo deep link + counts. */
    sessionId?: string;
    comboId?: string | null;
    terminalId?: string;
    sessionStatus?: string;
    counts?: { pending: number; awaiting: number; overdue: number };
    blocks?: { blockId: string; section: string; status: string; exportedAtUtc: string | null; legs: number }[];
}

export function parsePayload(item: ActionItemWireDto): ActionItemPayload {
    if (!item.payload) {
        return {};
    }
    try {
        return JSON.parse(item.payload) as ActionItemPayload;
    } catch {
        return {};
    }
}

/** The portfolio id segment of a subject path ("APG/{id}[/Hedging]" / "HedgingGrid/{id}"). */
export function subjectPortfolioId(subjectPath: string): string | null {
    const parts = (subjectPath || '').split('/');
    return parts.length >= 2 ? parts[1] : null;
}

/** Message-bus request the host serves by opening a panel on the item's subject (§4.2). */
export interface OpenSubjectRequest {
    itemType?: string;
    /** CR-20260929-001: 'PortfolioHedgingAnalysis' opens the analysis panel on a Collars portfolio.
     * CR-20260930-001 M4 (owner): an analysis overdue card opens COLLARS ('Portfolio Hedging') —
     * the acquisition happens there — with the combo auto-selected. */
    panel: 'APG' | 'HedgingGrid' | 'PortfolioHedgingAnalysis' | 'Collars';
    userId: string;
    portfolioId: string;
    /** CR-20260929-001 (ruling R11): the Collars COMBO the analysis panel loads; the unit of analysis. */
    comboId?: string | null;
    /** For ExpiringToday: the expiry the Hedging Grid should select. */
    expirationDayKey: string | null;
    /** The item, for the panel's own highlighting. */
    actionItemId: string;
    /** CR-20260924-001 F4b: the ONE panel instance that must take the request (the active panel of
     * that kind, else the first); null = a panel being added now takes it. Every other instance ignores it. */
    targetPanelId: string | null;
    /** Action Board navigation stays in its originating layout. */
    sourceLayoutTabId?: string;
    /** CR-20260927-003: set for a ZoneDrift card only - the market zone the card names. APG then
     * auto-fires ONE #2B move-to-zone pricing (Override ATM dialog pre-filled with this zone).
     * null = a plain open (portfolio loaded, nothing priced). */
    zoneDrift: { zone: number } | null;
    at: number;
}

export const OPEN_SUBJECT_REQUEST_TOPIC = 'ActionBoard.OpenSubjectRequest';
export const APG_SELECT_PORTFOLIO_REQUEST_TOPIC = 'Apg.SelectPortfolioRequest';
export const HG_SELECT_PORTFOLIO_REQUEST_TOPIC = 'Hg.SelectPortfolioRequest';
/** CR-20260929-001: the Portfolio Hedging Analysis panel's select request (a Collars portfolio id). */
export const PHA_SELECT_PORTFOLIO_REQUEST_TOPIC = 'Pha.SelectPortfolioRequest';
/** CR-20260930-001 M4: the Collars panel's select request (a Collars COMBO id in `comboId`). */
export const COLLARS_SELECT_COMBO_REQUEST_TOPIC = 'Collars.SelectComboRequest';
/** A select request older than this is stale and ignored by a panel booting later. */
export const SELECT_REQUEST_TTL_MS = 20000;
